/**
 * The right column with a session open — the T-A5 panel: the status badge, who, what and when, the
 * facts about the booking (the free-cancellation moment, what it did to the client's credits), and
 * the action rows under the divider.
 *
 * Which rows stand is `model/sessionActions`' — mobile's trainer sheet, ported — and the writes
 * behind them are `useSessionActions`'. "Przełóż" opens its form in place of the rows; "Profil"
 * and "Przejrzyj sesję" are links (the client's card, and the session's own overlay on it); the
 * four writes each ask first in the app's one confirm (`shared/ui/ConfirmDialog`). A confirmed act
 * hands focus to the panel's heading, because the row that asked may be gone; Wróć or Escape hands
 * it back to the row. The facts and the rows wait for the credit reads together, as bars in their
 * own places, so the forgiveness row and the credit lines arrive with the rest.
 *
 * The panel is mounted per session (`key` in `CalendarTab`): a refusal, a confirm or an open form
 * belongs to the session it was raised on.
 *
 * The panel takes focus when it opens (its heading), and Escape or the back control returns the
 * column to the day's list.
 */

import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ComponentType } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { CalendarIcon } from '~/shared/ui/AuthIcons';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { CheckGlyph, CloseGlyph } from '~/shared/ui/FormGlyphs';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import chip from '~/shared/ui/neutralChip.module.css';
import skeleton from '~/shared/ui/skeleton.module.css';
import { ChevronGlyph, LoopGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useSessionActions } from '../lib/useSessionActions';
import type { BookingSession } from '../model/bookingSession';
import type { TimeSpan } from '../model/busyTime';
import { weekdayDate } from '../model/calendarWords';
import type { PillTone } from '../model/dayAgenda';
import { isConfirmedAct, sessionState, type SessionActionId } from '../model/sessionActions';
import { sessionDetail } from '../model/sessionDetail';
import { BanGlyph, ClientGlyph, ListGlyph, MessageGlyph, UndoGlyph } from './BookingGlyphs';
import compose from './ComposeForm.module.css';
import { RescheduleForm } from './RescheduleForm';
import { SessionCall } from './SessionCall';
import { SessionFacts } from './SessionFacts';
import styles from './SidePanel.module.css';

const DOT_CLASS: Readonly<Record<PillTone, string | undefined>> = {
  good: styles.dotGood,
  accent: styles.dotAccent,
  warn: styles.dotWarn,
  danger: styles.dotDanger,
  neutral: styles.dotNeutral,
};

const ROW_GLYPH: Readonly<Record<SessionActionId, ComponentType<{ className?: string }>>> = {
  reschedule: LoopGlyph,
  message: MessageGlyph,
  profile: ClientGlyph,
  cancel: BanGlyph,
  markNoShow: CloseGlyph,
  markHeld: CheckGlyph,
  forgiveCharge: UndoGlyph,
  review: ListGlyph,
};

const ROW_LABEL: Readonly<Record<SessionActionId, string>> = {
  reschedule: 'calendar.sheet.action.reschedule',
  message: 'calendar.sheet.action.message',
  profile: 'calendar.sheet.action.profile',
  cancel: 'calendar.sheet.action.cancel',
  markNoShow: 'calendar.sheet.action.markNoShow',
  markHeld: 'calendar.sheet.action.markHeld',
  forgiveCharge: 'web:booking.actions.forgiveCharge',
  review: 'calendar.sheet.action.review',
};

interface SessionDetailPanelProps {
  readonly session: BookingSession;
  readonly zone: string;
  readonly nowMs: number;
  /** Why the last move went back, said here too while the form is open. */
  readonly moveRefusal: string | null;
  readonly onReschedule: (target: TimeSpan) => Promise<boolean>;
  readonly onMoveEdit: () => void;
  readonly onClose: () => void;
}

export function SessionDetailPanel({
  session,
  zone,
  nowMs,
  moveRefusal,
  onReschedule,
  onMoveEdit,
  onClose,
}: SessionDetailPanelProps) {
  const { t, i18n } = useTranslation();
  const heading = useRef<HTMLHeadingElement>(null);
  const moveRow = useRef<HTMLButtonElement>(null);
  const [moving, setMoving] = useState(false);
  const detail = sessionDetail(session, nowMs);
  const acts = useSessionActions(session, { zone, nowMs });
  const locale = i18n.language;
  const name = session.clientName ?? t('web:booking.unnamedClient');

  // A new session in the column is a new subject: the reader is taken to its name.
  useEffect(() => {
    heading.current?.focus();
  }, [session.id]);

  return (
    <div className={styles.detail} aria-busy={acts.gate.waiting}>
      <button type="button" className={styles.back} onClick={onClose}>
        <ChevronGlyph className={styles.backGlyph} direction="left" />
        {t('web:booking.detail.back')}
      </button>

      <span
        className={`${String(chip.neutralChip)} ${String(styles.pill)} ${String(styles.badge)}`}
      >
        <i
          className={`${String(styles.dot)} ${String(DOT_CLASS[detail.badge.tone])}`}
          aria-hidden="true"
        />
        {t(detail.badge.labelKey, detail.badge.params)}
      </span>

      <div>
        <h2 ref={heading} tabIndex={-1} className={styles.detailName}>
          {name}
        </h2>
        <p className={styles.detailSub}>
          {`${t(detail.kindKey)} · ${t('web:booking.detail.minutes', { count: session.durationMinutes })} · ${t(detail.placeKey)}`}
          {detail.clientBooked ? (
            <>
              {' · '}
              <span className={styles.originTag}>{t('web:booking.detail.clientBooked')}</span>
            </>
          ) : null}
        </p>
      </div>

      {session.day === null ? null : (
        <p className={styles.when}>
          <CalendarIcon className={styles.whenGlyph} />
          {[weekdayDate(session.day, locale), session.timeLabel].filter(Boolean).join(' · ')}
        </p>
      )}

      <SessionCall session={session} />

      <SessionFacts
        detail={detail}
        credit={acts.credit}
        gate={acts.gate}
        zone={zone}
        retrying={acts.creditsRetrying}
        onRetryCredits={acts.retryCredits}
      />

      {sessionState(session, nowMs) === 'consultAwaiting' ? (
        <p className={styles.verdictNote}>{t('web:booking.actions.verdictNote')}</p>
      ) : null}

      {acts.refusal === null ? null : (
        <p className={compose.refusal} role="alert">
          {acts.refusal}
        </p>
      )}

      {acts.actions.length === 0 ? null : <hr className={styles.rule} />}
      {acts.gate.waiting ? (
        <ul className={styles.actions} aria-hidden="true">
          {acts.actions.map((action) => (
            <li
              key={action.id}
              className={`${acts.gate.skeleton ? String(skeleton.bar) : ''} ${String(styles.actionPending)}`}
            />
          ))}
        </ul>
      ) : moving ? (
        <>
          <RescheduleForm
            session={session}
            zone={zone}
            nowMs={nowMs}
            onReschedule={onReschedule}
            onEdit={onMoveEdit}
            onDone={(movedToAnotherWeek) => {
              // The row comes back in this same commit, so focus can land on it at once.
              flushSync(() => {
                setMoving(false);
              });
              // Moved to another week, the grid went with it and the session is a new subject:
              // to its name. Otherwise back to the row that opened the form.
              (movedToAnotherWeek ? heading.current : moveRow.current)?.focus();
            }}
          />
          {moveRefusal === null ? null : (
            <p className={compose.refusal} role="alert">
              {moveRefusal}
            </p>
          )}
        </>
      ) : (
        <ul
          className={`${String(styles.actions)} ${acts.gate.reveal}`}
          aria-label={t('web:booking.actions.label')}
        >
          {acts.actions.map((action) => {
            const Glyph = ROW_GLYPH[action.id];
            const className = `${String(styles.action)} ${action.role === 'danger' ? String(styles.actionDanger) : ''}`;
            const content = (
              <>
                <Glyph className={styles.actionGlyph} />
                <span className={styles.actionLabel}>{t(ROW_LABEL[action.id])}</span>
                <ChevronGlyph className={styles.actionChevron} direction="right" />
              </>
            );
            return (
              <li key={action.id}>
                {action.id === 'message' ? (
                  // Mobile's "Napisz", where mobile puts it: the client's conversation on `/czat`
                  // (since 5 October 2026; it was refused while the chat was mobile-only).
                  <Link className={className} to="/czat" search={{ klient: session.customerId }}>
                    {content}
                  </Link>
                ) : action.id === 'profile' || action.id === 'review' ? (
                  <Link
                    className={className}
                    to="/klienci/$clientId"
                    params={{ clientId: String(session.customerId) }}
                    search={
                      action.id === 'review' && session.executionId !== null
                        ? { exec: session.executionId }
                        : {}
                    }
                  >
                    {content}
                  </Link>
                ) : (
                  <button
                    ref={action.id === 'reschedule' ? moveRow : undefined}
                    type="button"
                    className={className}
                    onClick={() => {
                      if (action.id === 'reschedule') setMoving(true);
                      else if (isConfirmedAct(action.id)) acts.ask(action.id);
                    }}
                  >
                    {content}
                  </button>
                )}
              </li>
            );
          })}
          {acts.credit.kind === 'ready' && acts.credit.forgiveness === 'unknown' ? (
            // The forgiveness row's own slot, held while the ledger was read: why it is not offered.
            <li className={styles.actionUnavailable}>{t('web:booking.actions.forgiveUnavailable')}</li>
          ) : null}
        </ul>
      )}

      <ConfirmDialog
        prompt={acts.prompt}
        onConfirm={acts.confirm}
        onCancel={acts.dismiss}
        finalFocus={acts.landed ? heading : undefined}
      />
      {acts.gate.waiting ? (
        <span className={hidden.srOnly}>{t('web:booking.actions.loading')}</span>
      ) : null}
      <LiveRegionAnnouncer message={acts.announcement} />
    </div>
  );
}
